"use client";

import { Check, Sparkles } from "lucide-react";
import Link from "next/link";

import { CodePlanTracker } from "@/components/LandingTracker";
import { Button } from "@/components/ui/button";

import { useI18n } from "@llmgateway/i18n/react";
import { DEV_PLAN_PRICES, type DevPlanTier } from "@llmgateway/shared";

export type DevPlanCredits = Record<DevPlanTier, number>;

interface PlanContent {
	nameKey: string;
	descriptionKey: string;
	tier: DevPlanTier;
	taglineKey: string;
	popular?: boolean;
	featureKeys: string[];
}

const plans: PlanContent[] = [
	{
		nameKey: "pricing.plans.lite.name",
		tier: "lite",
		descriptionKey: "pricing.plans.lite.description",
		taglineKey: "pricing.plans.lite.tagline",
		featureKeys: [
			"pricing.plans.lite.feature1",
			"pricing.plans.lite.feature2",
			"pricing.plans.lite.feature3",
			"pricing.plans.lite.feature4",
		],
	},
	{
		nameKey: "pricing.plans.pro.name",
		tier: "pro",
		descriptionKey: "pricing.plans.pro.description",
		taglineKey: "pricing.plans.pro.tagline",
		popular: true,
		featureKeys: [
			"pricing.plans.pro.feature1",
			"pricing.plans.pro.feature2",
			"pricing.plans.pro.feature3",
			"pricing.plans.pro.feature4",
		],
	},
	{
		nameKey: "pricing.plans.max.name",
		tier: "max",
		descriptionKey: "pricing.plans.max.description",
		taglineKey: "pricing.plans.max.tagline",
		featureKeys: [
			"pricing.plans.max.feature1",
			"pricing.plans.max.feature2",
			"pricing.plans.max.feature3",
			"pricing.plans.max.feature4",
		],
	},
];

function formatUsd(amount: number): string {
	return Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(0)}`;
}

interface PricingPlansProps {
	credits: DevPlanCredits;
}

export function PricingPlans({ credits }: PricingPlansProps) {
	const { t } = useI18n();

	return (
		<div>
			<div className="grid gap-6 md:grid-cols-3">
				{plans.map((plan) => {
					const monthlyPrice = DEV_PLAN_PRICES[plan.tier];
					const displayPrice = monthlyPrice;
					const usageValue = credits[plan.tier];
					const ratio = usageValue / monthlyPrice;
					const usageWithSoulForge = Math.round(usageValue * 2);
					const planName = t(plan.nameKey);

					return (
						<div
							key={plan.tier}
							className={`relative flex flex-col rounded-2xl border bg-card p-7 transition-all ${
								plan.popular
									? "border-foreground/30 shadow-lg ring-1 ring-foreground/10"
									: "hover:shadow-md"
							}`}
						>
							{plan.popular && (
								<div className="absolute -top-3 left-6">
									<span className="rounded-full bg-foreground px-3 py-1 text-xs font-medium text-background">
										{t("pricing.popular")}
									</span>
								</div>
							)}

							<div className="mb-5">
								<h3 className="text-lg font-semibold">{planName}</h3>
								<p className="mt-1 text-sm text-muted-foreground">
									{t(plan.descriptionKey)}
								</p>
							</div>

							<div className="mb-1 flex items-baseline gap-1.5">
								<span className="text-5xl font-bold tracking-tight tabular-nums">
									${displayPrice}
								</span>
								<span className="text-muted-foreground">
									{t("pricing.perMonth")}
								</span>
							</div>
							<div className="mb-6 min-h-[20px] text-xs text-muted-foreground">
								{t(plan.taglineKey)}
							</div>

							{/* Baseline: what you pay vs. what you actually get */}
							<div className="mb-5 rounded-xl border border-dashed bg-muted/40 p-4">
								<div className="mb-3 flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
									<span>{t("pricing.whatYouGet")}</span>
									<span className="rounded-full bg-foreground/90 px-2 py-0.5 text-[10px] font-bold tabular-nums text-background">
										{t("pricing.valueLabel", { ratio })}
									</span>
								</div>
								<div className="space-y-2.5 text-sm">
									<div className="flex items-baseline justify-between">
										<span className="text-muted-foreground">
											{t("pricing.youPay")}
										</span>
										<span className="font-mono font-semibold tabular-nums">
											${monthlyPrice}
											<span className="text-xs font-normal text-muted-foreground">
												{t("pricing.perMonth")}
											</span>
										</span>
									</div>
									<div className="flex items-baseline justify-between">
										<span className="text-muted-foreground">
											{t("pricing.youUse")}
										</span>
										<span className="font-mono font-semibold tabular-nums text-foreground">
											{formatUsd(usageValue)}
											<span className="text-xs font-normal text-muted-foreground">
												{" "}
												{t("pricing.providerRates")}
											</span>
										</span>
									</div>
									{/* Visual ratio bar */}
									<div className="pt-1">
										<div className="relative h-2 overflow-hidden rounded-full bg-foreground/10">
											<div
												className="absolute left-0 top-0 h-full rounded-full bg-foreground/30"
												style={{ width: `${(1 / ratio) * 100}%` }}
											/>
											<div className="absolute left-0 top-0 h-full w-full rounded-full ring-1 ring-inset ring-foreground/10" />
										</div>
										<div className="mt-1.5 flex items-center justify-between text-[10px] text-muted-foreground tabular-nums">
											<span>
												{t("pricing.paid", {
													amount: `$${monthlyPrice}`,
												})}
											</span>
											<span>
												{t("pricing.used", {
													amount: formatUsd(usageValue),
												})}
											</span>
										</div>
									</div>
								</div>
								{/* SoulForge boost callout */}
								<div className="mt-4 flex items-start gap-2 rounded-lg bg-emerald-500/10 px-3 py-2.5 text-xs text-emerald-900 dark:text-emerald-300">
									<Sparkles
										className="mt-0.5 h-3.5 w-3.5 shrink-0"
										strokeWidth={2}
									/>
									<div>
										{t("pricing.withSoulForge", {
											amount: formatUsd(usageValue),
											effective: formatUsd(usageWithSoulForge),
										})}
									</div>
								</div>
							</div>

							<ul className="mb-7 flex-1 space-y-2.5">
								{plan.featureKeys.map((featureKey) => (
									<li key={featureKey} className="flex items-start gap-2.5">
										<Check className="mt-0.5 h-4 w-4 shrink-0 text-foreground/70" />
										<span className="text-sm text-muted-foreground">
											{t(featureKey)}
										</span>
									</li>
								))}
							</ul>

							<CodePlanTracker plan={plan.tier} price={displayPrice}>
								<Button
									className="w-full"
									size="lg"
									variant={plan.popular ? "default" : "outline"}
									asChild
								>
									<Link href={`/signup?plan=${plan.tier}`}>
										{t("pricing.getPlan", { plan: planName })}
									</Link>
								</Button>
							</CodePlanTracker>
						</div>
					);
				})}
			</div>
			<InvoiceInfoLabel />
			<p className="mt-3 text-center text-xs text-muted-foreground">
				{t("pricing.usageMetered")}
			</p>
		</div>
	);
}

function InvoiceInfoLabel() {
	const { t } = useI18n();

	return (
		<p className="mx-auto mt-4 max-w-2xl text-center text-[11px] leading-relaxed text-muted-foreground">
			{t("pricing.invoiceInfo")}
		</p>
	);
}
